import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Plus, Search, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "../components/AppShell";
import { StatCounters } from "../components/dashboard/StatCounters";
import { ClientTable } from "../components/dashboard/ClientTable";
import { PaginationBar } from "../components/dashboard/PaginationBar";
import { ImportExcelDialog } from "../components/dashboard/ImportExcelDialog";
import { ReviewClients } from "../components/dashboard/ReviewClients";
import { DuplicateClients } from "../components/dashboard/DuplicateClients";
import { canWrite, isAdmin, useAuth } from "../context/AuthContext";
import { DriveNotice } from "../components/dashboard/DriveNotice";
import { api } from "../lib/api";

export default function DashboardPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const page = Number(params.get("pagina") || 1);
  const [term, setTerm] = useState(q);
  const [stats, setStats] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();

  const update = (next) => {
    const merged = { q, pagina: String(page), ...next };
    const clean = Object.fromEntries(Object.entries(merged).filter(([k, v]) => v && !(k === "pagina" && v === "1")));
    setParams(clean, { replace: true });
  };

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => toast.error("No se pudieron cargar los contadores"));
  }, [reloadKey]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (term !== q) update({ q: term.trim() ? term : "", pagina: "1" });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get("/clients", { params: { q, page, page_size: 25 } })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && toast.error("No se pudo cargar la lista de clientes"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [q, page, reloadKey]);

  return (
    <AppShell>
      <section className="tr-fade-up space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="tr-eyebrow">Panel de expedientes</div>
            <h1 className="text-2xl font-extrabold tracking-tight text-[#182235] sm:text-3xl">Clientes y expedientes</h1>
          </div>
          {canWrite(user) && (
            <div className="flex flex-wrap gap-2" data-testid="dashboard-actions">
              {isAdmin(user) && <ImportExcelDialog onImported={() => setReloadKey((k) => k + 1)} />}
              {isAdmin(user) && (
                <button type="button" onClick={() => navigate("/ia")} className="tr-btn-light px-4 py-2.5 text-sm" data-testid="dashboard-ai-button">
                  <Sparkles className="h-4 w-4" /> Clasificar con IA
                </button>
              )}
              <button type="button" onClick={() => navigate("/creditos/nuevo")} className="tr-btn-primary px-4 py-2.5 text-sm" data-testid="add-loan-button">
                <Plus className="h-4 w-4" /> Agregar crédito
              </button>
            </div>
          )}
        </div>
        <div className="relative max-w-2xl">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#5b667a]" />
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Buscar por nombre, ID de cliente o ID de crédito"
            className="h-12 w-full rounded-[14px] border border-[#d7e0ec] bg-white pl-12 pr-11 text-sm text-[#182235] shadow-[0_8px_24px_rgba(14,22,38,0.05)] outline-none transition-colors focus:border-[#0f5ef7] focus:ring-2 focus:ring-[#cfe0ff]"
            data-testid="client-search-input"
            aria-label="Buscar cliente"
          />
          {term && (
            <button type="button" onClick={() => setTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-[#5b667a] hover:bg-[#eef4ff] hover:text-[#0a47be]" data-testid="client-search-clear" aria-label="Limpiar búsqueda">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </section>

      <StatCounters stats={stats} />

      {isAdmin(user) && <DriveNotice />}
      {isAdmin(user) && <ReviewClients onChanged={() => setReloadKey((k) => k + 1)} />}
      {isAdmin(user) && <DuplicateClients key={reloadKey} onChanged={() => setReloadKey((k) => k + 1)} />}

      <section className="tr-card overflow-hidden" data-testid="clients-section">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#e8edf5] px-4 py-4 sm:px-5">
          <h2 className="text-base font-bold text-[#182235] md:text-lg">Lista de clientes</h2>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-[#0f5ef7]" data-testid="clients-loading" />}
        </div>
        {data && <ClientTable items={data.items} />}
        {data && <PaginationBar page={data.page} pages={data.pages} total={data.total} pageSize={data.page_size} onPage={(p) => update({ pagina: String(p) })} />}
      </section>
    </AppShell>
  );
}
